import AddIcon from "@mui/icons-material/Add";
import { Box, ButtonBase } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { DISABLED_CONTROL, MOTION, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { TOUCH_TARGET_SX } from "../../UIToolkit/touchTarget";

type Props = {
	groupKey: string;
	onClick: () => void;
	/**
	 * The id of the line saying why nothing may be posted under this group (`isSectionWriteBlocked`): the button is off
	 * and described by it. `undefined` for a group that may be posted to.
	 */
	blockedReasonId: string | undefined;
};

/** The artifact's `.gadd` tile. The hit area around it is still `TOUCH_TARGET_SX`. */
const POST_TILE_SIZE = 26;

/** A group header's `+` — post a shift for this window. A 26px tile inside a finger-sized hit area: the artifact's density, without a small target. */
export const GapsGroupPostButton: FC<Props> = ({ groupKey, onClick, blockedReasonId }) => {
	const { t } = useTranslation();
	return (
		<ButtonBase
			data-testid={`gaps-group-post-${groupKey}`}
			aria-label={t("gaps.group.postShift")}
			onClick={onClick}
			disabled={blockedReasonId !== undefined}
			aria-describedby={blockedReasonId}
			sx={{
				...TOUCH_TARGET_SX,
				flex: "none",
				px: 1,
				"&:hover .gaps-post-tile": { bgcolor: "primary.main", color: SURFACE, borderColor: "primary.main" },
				"&.Mui-disabled .gaps-post-tile": { bgcolor: DISABLED_CONTROL.FILL, color: DISABLED_CONTROL.INK, borderColor: DISABLED_CONTROL.BORDER },
			}}
		>
			<Box
				component="span"
				className="gaps-post-tile"
				sx={{
					width: POST_TILE_SIZE,
					height: POST_TILE_SIZE,
					display: "grid",
					placeItems: "center",
					borderRadius: "7px",
					border: `1px solid ${NEUTRAL[200]}`,
					bgcolor: SURFACE,
					color: "primary.dark",
					transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				}}
			>
				<AddIcon sx={{ fontSize: 16 }} />
			</Box>
		</ButtonBase>
	);
};
